import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getLead,
  qualifyLead,
  generateFollowup,
  generateLinkedinDm,
  handleObjection,
  sendLeadEmail,
  deleteLead,
  generateMeeting,
  generateConfirmation,
  markReplied,
  getConfig,
  patchLead,
  smartReply,
  salesBrain,
  regenerateInLanguage,
  getVoiceConfig,
  callLead,
  improveEmail,
  generateSequence,
  updateLeadAutonomousMode,
  getCalendarAvailability,
  bookCalendarMeeting,
  sendWhatsAppMessage,
} from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Sparkles,
  Send,
  Copy,
  Linkedin,
  MessageSquareWarning,
  Mail,
  Trash2,
  CheckCircle2,
  Calendar,
  Reply,
  Phone,
  Zap,
  TrendingUp,
  Flame,
  Save,
  Inbox,
  Brain,
  RefreshCw,
  Languages,
  ListOrdered,
  Clock,
  X,
  Bot,
  Lightbulb,
  Wand2,
  MessageCircle,
  Lock,
  CalendarCheck,
} from "lucide-react";

// Pure helpers — extracted from nested ternaries for readability.
// Defined at module scope so they're not recreated on every render.
const getScoreColor = (score, highThreshold, midThreshold) => {
  if (score == null) return "#6B7280";
  if (score >= highThreshold) return "#16A34A";
  if (score >= midThreshold) return "#002FA7";
  return "#DC2626";
};

const getPriorityClass = (priority) => {
  if (priority === "High") return "bg-red-50 text-red-700 border-red-200";
  if (priority === "Medium") return "bg-amber-50 text-amber-800 border-amber-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const getIcpStatusText = (icp) => {
  if (icp == null) return "Run qualification to score this lead.";
  if (icp >= 60) return "Proceed — strong fit.";
  return "Below threshold. Consider rejecting.";
};

const getRegenLabel = (isBusy, busyText, hasContent, regenText, newText) => {
  if (isBusy) return busyText;
  if (hasContent) return regenText;
  return newText;
};

const SENTIMENT_STYLES = {
  positive: { borderColor: "#16A34A", color: "#166534", background: "#F0FDF4" },
  negative: { borderColor: "#DC2626", color: "#991B1B", background: "#FEF2F2" },
  neutral: { borderColor: "#E5E7EB", color: "#111827", background: "#F9FAFB" },
};
const getSentimentStyle = (sentiment) =>
  SENTIMENT_STYLES[sentiment] || SENTIMENT_STYLES.neutral;

const getLifecycleStepClass = (isActive, isPast) => {
  if (isActive) return "bg-[#002FA7] text-white border-[#002FA7]";
  if (isPast) return "bg-blue-50 text-[#002FA7] border-blue-200";
  return "bg-white text-slate-500 border-slate-200";
};

// AI Sales Brain — action-tone → chip styling
const ACTION_TONE_CLASS = {
  send: "bg-green-50 text-green-800 border-green-200",
  wait: "bg-amber-50 text-amber-800 border-amber-200",
  move: "bg-[#002FA7]/10 text-[#002FA7] border-blue-200",
  skip: "bg-red-50 text-red-700 border-red-200",
};
const getActionToneClass = (tone) =>
  ACTION_TONE_CLASS[tone] || ACTION_TONE_CLASS.send;

// Deal Intent (Prompt 6) — High / Medium / Low chip styling
const INTENT_CLASS = {
  High: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Medium: "bg-amber-50 text-amber-800 border-amber-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
};
const getIntentClass = (label) =>
  INTENT_CLASS[label] || INTENT_CLASS.Medium;

const BRAIN_LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी · Hindi" },
  { code: "ar", label: "العربية · Arabic" },
  { code: "es", label: "Español · Spanish" },
  { code: "fr", label: "Français · French" },
  { code: "de", label: "Deutsch · German" },
];

// Animation / latency-masking constants (ms)
const BRAIN_MIN_LOADER_MS = 600;           // min dwell on "Analyzing…" for demo polish
const CALL_SIM_CONNECT_DELAY_MS = 1200;    // dialing → connected transition
const CALL_SIM_TALK_DELAY_MS = 2600;       // connected → talking transition
// Conversion score color thresholds (0-100)
const CONVERSION_HIGH_THRESHOLD = 75;
const CONVERSION_MID_THRESHOLD = 50;

// Per-lead autonomous override — compact segmented control in the header.
// "inherit" → workspace default, "on" → AI auto-executes, "off" → frozen.
const AUTONOMOUS_MODES = [
  {
    key: "inherit",
    label: "Inherit",
    tip: "Follow workspace execution mode (default).",
  },
  {
    key: "on",
    label: "On",
    tip: "AI runs autonomously on this lead — emails & calls without approval.",
  },
  {
    key: "off",
    label: "Off",
    tip: "Freeze automation for this lead — nothing fires until you approve.",
  },
];

function AutonomousModeToggle({ mode, disabled, onChange }) {
  const current = mode || "inherit";
  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-1"
        data-testid="autonomous-mode-toggle"
        data-current-mode={current}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1 pl-1 pr-1 text-[10px] font-semibold text-slate-500 label-caps">
              <Bot className="h-3 w-3" />
              Auto
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Per-lead automation override. Switch how the AI treats this one lead.
          </TooltipContent>
        </Tooltip>
        {AUTONOMOUS_MODES.map((m) => {
          const active = current === m.key;
          return (
            <Tooltip key={m.key}>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(m.key)}
                    data-testid={`autonomous-mode-${m.key}`}
                    data-state={active ? "on" : "off"}
                    className={`h-6 px-2 rounded text-[11px] font-semibold transition-colors ${
                      active
                        ? m.key === "on"
                          ? "bg-[#DC2626] text-white"
                          : m.key === "off"
                          ? "bg-slate-700 text-white"
                          : "bg-[#002FA7] text-white"
                        : "text-slate-600 hover:bg-white"
                    } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {m.label}
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">{m.tip}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

// ============ DEAL ACCELERATOR UI (surfacing lead.accelerator) ============
// Two additive bits:
//   - AcceleratorChip: a compact "Suggested: <next_action>" pill in the
//     LeadDetail header, with the response preview in a tooltip.
//   - AcceleratorSuggestion: a full card inside the Smart Reply tab with
//     response preview + "Use this reply" one-click action.

const ACCELERATOR_ACTION_LABEL = {
  call: "Call now",
  send_proof: "Send proof",
  offer_pilot: "Offer pilot",
  book_meeting: "Book a call",
  wait: "Hold + monitor",
  nurture: "Nurture",
};

const ACCELERATOR_ACTION_CLASS = {
  call: "bg-emerald-100 text-emerald-800 border-emerald-200",
  send_proof: "bg-blue-100 text-[#002FA7] border-blue-200",
  offer_pilot: "bg-violet-100 text-violet-800 border-violet-200",
  book_meeting: "bg-amber-100 text-amber-800 border-amber-200",
  wait: "bg-slate-100 text-slate-600 border-slate-200",
  nurture: "bg-slate-100 text-slate-600 border-slate-200",
};

function AcceleratorChip({ accelerator }) {
  if (!accelerator?.next_action) return null;
  const label =
    ACCELERATOR_ACTION_LABEL[accelerator.next_action] || accelerator.next_action;
  const cls =
    ACCELERATOR_ACTION_CLASS[accelerator.next_action] ||
    "bg-slate-100 text-slate-600 border-slate-200";
  const urgency = accelerator.urgency_level;
  const urgencyDot =
    urgency === "high" ? "bg-red-500"
    : urgency === "medium" ? "bg-amber-500"
    : urgency === "low" ? "bg-slate-300"
    : null;
  const preview = (accelerator.response || "").slice(0, 220);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border cursor-help ${cls}`}
            data-testid="accelerator-chip"
            data-next-action={accelerator.next_action}
            data-urgency={urgency || ""}
          >
            {urgencyDot && (
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${urgencyDot}`}
                title={`${urgency} urgency`}
                data-testid="accelerator-urgency-dot"
              />
            )}
            <Lightbulb className="h-3 w-3" />
            Suggested: {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-sm">
          <div className="text-[11px] font-semibold text-slate-500 label-caps mb-1">
            Accelerator reply preview
          </div>
          <div className="text-[12px] leading-relaxed whitespace-pre-wrap">
            {preview}
            {(accelerator.response || "").length > 220 ? "…" : ""}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function AcceleratorSuggestion({ accelerator, onUseReply }) {
  if (!accelerator?.response) return null;
  const actionLabel =
    ACCELERATOR_ACTION_LABEL[accelerator.next_action] ||
    accelerator.next_action ||
    "Next step";
  const actionCls =
    ACCELERATOR_ACTION_CLASS[accelerator.next_action] ||
    "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <div
      className="rounded-lg border border-[#002FA7]/15 bg-gradient-to-br from-[#F6F9FF] to-white p-4"
      data-testid="accelerator-suggestion"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[#002FA7] text-white flex items-center justify-center shrink-0">
            <Wand2 className="h-3.5 w-3.5" />
          </div>
          <div>
            <div className="label-caps text-[#002FA7]">
              Deal Accelerator suggestion
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Framework-driven reframe · non-salesy
            </div>
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-semibold border ${actionCls}`}
          data-testid="accelerator-next-action"
        >
          <Lightbulb className="h-2.5 w-2.5" />
          Suggested: {actionLabel}
        </span>
      </div>
      <p
        className="mt-3 text-[13px] text-slate-800 leading-relaxed whitespace-pre-wrap"
        data-testid="accelerator-response"
      >
        {accelerator.response}
      </p>
      {accelerator.reasoning && (
        <p
          className="mt-2 text-[11px] text-slate-500 italic"
          data-testid="accelerator-reasoning"
        >
          Why it works — {accelerator.reasoning}
        </p>
      )}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          onClick={() => onUseReply(accelerator.response)}
          className="h-8 bg-[#002FA7] hover:bg-[#002072] text-xs"
          data-testid="accelerator-use-reply-btn"
        >
          <Wand2 className="h-3.5 w-3.5 mr-1.5" />
          Use this reply
        </Button>
        {accelerator.stage && (
          <span
            className="text-[10px] font-semibold uppercase tracking-wider text-slate-500"
            data-testid="accelerator-stage"
          >
            Stage: {accelerator.stage}
          </span>
        )}
        {accelerator.buying_signal && accelerator.buying_signal !== "none" && (
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
              accelerator.buying_signal === "positive"
                ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                : accelerator.buying_signal === "negative"
                ? "text-red-700 bg-red-50 border-red-200"
                : accelerator.buying_signal === "hesitant"
                ? "text-amber-700 bg-amber-50 border-amber-200"
                : "text-slate-600 bg-slate-50 border-slate-200"
            }`}
            data-testid="accelerator-buying-signal"
            title={`Signal: ${accelerator.buying_signal}`}
          >
            Signal: {accelerator.buying_signal}
          </span>
        )}
        {accelerator.urgency_level && (
          <span
            className="text-[10px] font-semibold uppercase tracking-wider text-slate-500"
            data-testid="accelerator-urgency-level"
          >
            Urgency: {accelerator.urgency_level}
          </span>
        )}
        {accelerator.objection && accelerator.objection !== "none" && (
          <span
            className="text-[10px] font-semibold uppercase tracking-wider text-slate-500"
            data-testid="accelerator-objection"
          >
            Objection: {accelerator.objection.replace(/_/g, " ")}
          </span>
        )}
      </div>
    </div>
  );
}



export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [followupBody, setFollowupBody] = useState("");
  const [objection, setObjection] = useState("");
  const [slot1, setSlot1] = useState("Tue 11:00 AM");
  const [slot2, setSlot2] = useState("Thu 3:30 PM");
  const [config, setConfig] = useState(null);
  const [signal, setSignal] = useState("");
  const [signalSaving, setSignalSaving] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(null);
  const [brain, setBrain] = useState(null); // {explanation, action, action_tone, priority_label}
  const [voiceConfig, setVoiceConfig] = useState({ mode: "demo", vapi_enabled: false });
  const [simPhase, setSimPhase] = useState(null); // null | "calling" | "connected" | "talking"
  const [callError, setCallError] = useState(null); // { message, canSimulate }
  const [emailImprovement, setEmailImprovement] = useState(null); // {original_email, improved_email, improvements}
  const [sequence, setSequence] = useState(null); // {steps: [...], generated_at}
  // Pending "on" confirmation target.
  const [pendingAutoMode, setPendingAutoMode] = useState(null);
  // Pre-send preview modal — read-only subject + body check before Resend.
  const [previewOpen, setPreviewOpen] = useState(false);
  // Controlled active tab — lets accelerator "Use this reply" switch tabs.
  const [activeTab, setActiveTab] = useState("email");

  const load = useCallback(async () => {
    const data = await getLead(id);
    setLead(data);
    const subj = data.subject_lines?.[0] || data.sent_subject || "";
    setSubject(subj);
    setBody(data.final_email || "");
    setFollowupBody(data.followup_email || "");
    setSignal(data.recent_signal || "");
    if (data.sequence && Array.isArray(data.sequence) && data.sequence.length) {
      setSequence({ steps: data.sequence, generated_at: data.sequence_generated_at });
    }
  }, [id]);

  useEffect(() => {
    load().finally(() => setLoading(false));
    getConfig().then(setConfig).catch(() => {});
    getVoiceConfig().then(setVoiceConfig).catch(() => {});
  }, [load]);

  if (loading) {
    return (
      <div className="px-10 py-10 text-slate-500">Loading lead…</div>
    );
  }

  if (!lead) {
    return (
      <div className="px-10 py-10">
        <p className="text-slate-600">Lead not found.</p>
        <Link to="/leads" className="text-[#002FA7] underline">
          Back to leads
        </Link>
      </div>
    );
  }

  const onQualify = async () => {
    setBusy("qualify");
    try {
      await qualifyLead(id);
      await load();
      toast.success("Qualified");
    } catch (err) {
      toast.error("Failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onFollowup = async () => {
    setBusy("followup");
    try {
      const res = await generateFollowup(id);
      setFollowupBody(res.followup_email);
      await load();
      toast.success("Follow-up generated");
    } catch (err) {
      toast.error("Failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onLinkedin = async () => {
    setBusy("linkedin");
    try {
      await generateLinkedinDm(id);
      await load();
      toast.success("LinkedIn DM generated");
    } catch (err) {
      toast.error("Failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onObjection = async () => {
    if (!objection.trim()) {
      toast.error("Paste the objection text first");
      return;
    }
    setBusy("objection");
    try {
      await handleObjection(id, objection);
      await load();
      toast.success("Reply generated");
    } catch (err) {
      toast.error("Failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onMeeting = async () => {
    setBusy("meeting");
    try {
      const slots = [slot1, slot2].filter(Boolean);
      await generateMeeting(id, { slots });
      await load();
      toast.success("2 meeting variations ready");
    } catch (err) {
      toast.error("Failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onConfirmation = async () => {
    setBusy("confirmation");
    try {
      await generateConfirmation(id);
      await load();
      toast.success("Confirmation message ready");
    } catch (err) {
      toast.error("Failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onMarkReplied = async () => {
    setBusy("replied");
    try {
      await markReplied(id);
      await load();
      toast.success("Marked as replied");
    } catch (err) {
      toast.error("Failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onSaveSignal = async () => {
    setSignalSaving(true);
    try {
      await patchLead(id, { recent_signal: signal });
      await load();
      toast.success("Signal saved", {
        description:
          "Regenerate the pipeline to use it in the opening line.",
      });
    } catch (err) {
      toast.error("Save failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSignalSaving(false);
    }
  };

  const onSmartReply = async () => {
    if (!replyText.trim()) {
      toast.error("Paste the prospect's reply first");
      return;
    }
    setBusy("smart-reply");
    try {
      const res = await smartReply(id, replyText);
      await load();
      toast.success(
        `Detected: ${res.intent} · ${res.sentiment}`,
        { description: `Next: ${res.suggested_next_action}` },
      );
    } catch (err) {
      toast.error("Smart reply failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onExplainLead = async () => {
    setBusy("brain");
    setBrain(null);
    // 600ms minimum loading time for demo realism ("Analyzing lead...")
    const start = Date.now();
    try {
      const res = await salesBrain(id);
      const elapsed = Date.now() - start;
      if (elapsed < BRAIN_MIN_LOADER_MS) {
        await new Promise((r) => setTimeout(r, BRAIN_MIN_LOADER_MS - elapsed));
      }
      setBrain(res);
    } catch (err) {
      toast.error("Sales Brain failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onRegenerateLanguage = async (targetLang) => {
    if (!targetLang || targetLang === lead.email_language) return;
    if (isLeadDisqualified(lead)) {
      toast.error("Lead is disqualified", {
        description: "Cannot regenerate outreach for rejected leads.",
      });
      return;
    }
    setBusy("regen-lang");
    try {
      await regenerateInLanguage(id, targetLang);
      await load();
      toast.success(`Regenerated in ${targetLang.toUpperCase()}`);
    } catch (err) {
      toast.error("Regeneration failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onCallLead = async (forceSimulate = false) => {
    if (isLeadDisqualified(lead)) {
      toast.error("Lead is disqualified", {
        description: "Cannot call rejected leads.",
      });
      return;
    }
    setBusy("call");
    setSimPhase("calling");
    setCallError(null);
    // Staged state progression — masks LLM latency with realistic call phases
    const p1 = setTimeout(() => setSimPhase("connected"), CALL_SIM_CONNECT_DELAY_MS);
    const p2 = setTimeout(() => setSimPhase("talking"), CALL_SIM_TALK_DELAY_MS);
    try {
      await callLead(id, null, forceSimulate);
      await load();
    } catch (err) {
      // Backend may return detail as {message, can_simulate} object OR plain string.
      const detail = err?.response?.data?.detail;
      const msg =
        (detail && typeof detail === "object" && detail.message) ||
        (typeof detail === "string" && detail) ||
        err.message;
      const canSimulate =
        !!(detail && typeof detail === "object" && detail.can_simulate) &&
        !forceSimulate;
      setCallError({ message: msg, canSimulate });
      toast.error("Call failed", { description: msg });
    } finally {
      clearTimeout(p1);
      clearTimeout(p2);
      setSimPhase(null);
      setBusy(null);
    }
  };

  const onImproveEmail = async () => {
    setBusy("improve-email");
    setEmailImprovement(null);
    try {
      const res = await improveEmail(id);
      setEmailImprovement(res);
    } catch (err) {
      toast.error("Improvement failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onAcceptImprovement = () => {
    if (!emailImprovement?.improved_email) return;
    setBody(emailImprovement.improved_email);
    setEmailImprovement(null);
    toast.success("Email replaced with improved version", {
      description: "Review and send when ready.",
    });
  };

  const onGenerateSequence = async () => {
    setBusy("sequence");
    try {
      const res = await generateSequence(id);
      setSequence({ steps: res.steps, generated_at: res.generated_at });
      toast.success("5-step sequence ready", {
        description: "Each step uses a different angle.",
      });
    } catch (err) {
      toast.error("Sequence generation failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const applyAutonomousMode = async (nextMode) => {
    if (!lead) return;
    const current = lead.autonomous_mode || "inherit";
    if (nextMode === current) return;
    setBusy("autonomous-mode");
    try {
      const updated = await updateLeadAutonomousMode(id, nextMode);
      setLead(updated);
      toast.success(
        nextMode === "on"
          ? "AI will now run autonomously on this lead"
          : nextMode === "off"
          ? "AI paused for this lead — requires approval"
          : "Following workspace default",
      );
    } catch (err) {
      toast.error("Could not update autonomous mode", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onSelectAutonomousMode = (nextMode) => {
    if (!nextMode) return; // ToggleGroup can deselect
    if (nextMode === "on") {
      // Require confirmation before enabling auto-send on a single lead.
      setPendingAutoMode(nextMode);
      return;
    }
    applyAutonomousMode(nextMode);
  };

  const isLeadDisqualified = (l) => {
    const lc = (l?.lifecycle || "").toUpperCase();
    const st = (l?.status || "").toLowerCase();
    return lc === "REJECTED" || lc === "INVALID" || st === "rejected" || st === "invalid";
  };

  const onSend = async () => {
    if (isLeadDisqualified(lead)) {
      toast.error("Lead is disqualified", {
        description: "Rejected/invalid leads cannot be emailed.",
      });
      return;
    }
    if (!body.trim()) {
      toast.error("Email body is empty");
      return;
    }
    setBusy("send");
    try {
      await sendLeadEmail(id, { subject, body });
      await load();
      toast.success(`Sent to ${lead.email}`);
    } catch (err) {
      toast.error("Send failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onSendTest = async () => {
    if (isLeadDisqualified(lead)) {
      toast.error("Lead is disqualified", {
        description: "Rejected/invalid leads cannot be emailed, even in test mode.",
      });
      return;
    }
    if (!body.trim()) {
      toast.error("Email body is empty");
      return;
    }
    const target = config?.test_email_target;
    if (!target) {
      toast.error("No test recipient configured", {
        description: "Set TEST_EMAIL_OVERRIDE or log in with a valid email.",
      });
      return;
    }
    setBusy("send-test");
    try {
      await sendLeadEmail(id, { subject, body, test_mode: true });
      await load();
      toast.success(`Test sent to ${target}`, {
        description: `Content tied to ${lead.name || "this lead"} — only recipient is redirected.`,
      });
    } catch (err) {
      toast.error("Test send failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const onSendFollowup = async () => {
    if (isLeadDisqualified(lead)) {
      toast.error("Lead is disqualified", {
        description: "Rejected/invalid leads cannot be emailed.",
      });
      return;
    }
    if (!followupBody.trim()) {
      toast.error("Generate follow-up first");
      return;
    }
    setBusy("send-followup");
    try {
      await sendLeadEmail(id, {
        subject: "Quick follow-up",
        body: followupBody,
        use_followup: true,
      });
      await load();
      toast.success("Follow-up sent");
    } catch (err) {
      toast.error("Send failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this lead?")) return;
    await deleteLead(id);
    toast.success("Lead deleted");
    navigate("/leads");
  };

  const icp = lead.icp_score ?? null;
  const icpColor = getScoreColor(icp, 80, 60);

  return (
    <div className="px-6 md:px-10 py-8 space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link
            to="/leads"
            className="mt-1 text-slate-500 hover:text-slate-900"
            data-testid="back-to-leads"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <div className="label-caps text-[#002FA7]">Lead</div>
            <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">
              {lead.name || "Unnamed"}
            </h1>
            <div className="mt-1 text-[14px] text-slate-600">
              {lead.title || "—"}
              {lead.company && (
                <>
                  {" "}
                  · <span className="font-medium">{lead.company}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={lead.status} />
          {lead.intent_label && (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border ${getIntentClass(
                lead.intent_label,
              )}`}
              title={lead.intent_reason || ""}
              data-testid="intent-chip"
            >
              <Flame className="h-3 w-3" />
              {lead.intent_label} Intent
            </span>
          )}
          {!isLeadDisqualified(lead) && (
            <AutonomousModeToggle
              mode={lead.autonomous_mode || "inherit"}
              disabled={busy === "autonomous-mode"}
              onChange={onSelectAutonomousMode}
            />
          )}
          {lead.accelerator?.next_action && (
            <AcceleratorChip accelerator={lead.accelerator} />
          )}
          {lead.lifecycle !== "REPLIED" &&
            lead.lifecycle !== "MEETING_BOOKED" &&
            lead.status === "sent" && (
              <Button
                size="sm"
                variant="outline"
                onClick={onMarkReplied}
                disabled={busy === "replied"}
                data-testid="mark-replied-btn"
                className="h-9 border-slate-200"
              >
                <Reply className="h-3.5 w-3.5 mr-1" /> Mark replied
              </Button>
            )}
          {!isLeadDisqualified(lead) && lead.lifecycle !== "MEETING_BOOKED" && (
            <CalendarBookingDialog
              leadId={lead.id}
              leadName={lead.name}
              onBooked={() => load()}
            />
          )}
          {!isLeadDisqualified(lead) && (
            <WhatsAppButton leadId={lead.id} leadName={lead.name} />
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            className="h-9 border-slate-200 text-slate-500 hover:text-red-600"
            data-testid="delete-lead-btn"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
        </div>
      </div>

      {/* State machine strip */}
      <StateMachineStrip lead={lead} />

      {/* Bento Grid: left = meta, right = editor/tabs */}
      <div className="grid grid-cols-1 lg:grid-cols-8 gap-4">
        {/* LEFT */}
        <div className="lg:col-span-3 space-y-4">
          {/* ICP + Conversion card */}
          <div className="rounded-md border border-slate-200 bg-white p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="label-caps">ICP Fit</div>
                <div
                  className="mt-2 font-display text-4xl font-extrabold tracking-tight leading-none"
                  style={{ color: icpColor }}
                  data-testid="icp-score"
                >
                  {icp ?? "—"}
                  <span className="text-slate-400 font-normal text-lg">
                    /100
                  </span>
                </div>
              </div>
              <div>
                <div className="label-caps flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> Conversion
                </div>
                <div
                  className="mt-2 font-display text-4xl font-extrabold tracking-tight leading-none"
                  style={{ color: getScoreColor(lead.conversion_score, CONVERSION_HIGH_THRESHOLD, CONVERSION_MID_THRESHOLD) }}
                  data-testid="conversion-score"
                >
                  {lead.conversion_score ?? "—"}
                  <span className="text-slate-400 font-normal text-lg">
                    /100
                  </span>
                </div>
              </div>
            </div>
            {lead.priority && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border ${getPriorityClass(
                      lead.priority,
                    )}`}
                    data-testid="priority-chip"
                  >
                    <Flame className="h-3 w-3" /> {lead.priority} Priority
                  </span>
                </div>
                {lead.priority_reason && (
                  <p className="mt-2 text-[12px] text-slate-600 leading-relaxed">
                    {lead.priority_reason}
                  </p>
                )}
              </div>
            )}
            <div className="mt-4 text-[12.5px] text-slate-600">
              {getIcpStatusText(icp)}
            </div>
            {icp == null && (
              <Button
                onClick={onQualify}
                disabled={busy === "qualify"}
                data-testid="qualify-lead-btn"
                className="mt-4 w-full bg-[#002FA7] hover:bg-[#002072]"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {busy === "qualify" ? "Running…" : "Run qualification"}
              </Button>
            )}
            {icp != null && (
              <Button
                variant="outline"
                size="sm"
                onClick={onQualify}
                disabled={busy === "qualify"}
                data-testid="requalify-lead-btn"
                className="mt-4 w-full border-slate-200"
              >
                Regenerate pipeline
              </Button>
            )}
          </div>

          {/* Recent Company Signal */}
          <div className="rounded-md border border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="label-caps">Recent Company Signal</div>
              {signal && (
                <span className="chip text-[#002FA7] border-blue-200 bg-blue-50">
                  <Zap className="h-3 w-3" /> active
                </span>
              )}
            </div>
            <p className="mt-2 text-[12px] text-slate-500 leading-relaxed">
              Paste any news — funding, hiring spike, product launch. The AI
              will open the email with it if natural.
            </p>
            <Textarea
              value={signal}
              onChange={(e) => setSignal(e.target.value)}
              placeholder="e.g. Raised $2M seed round last month"
              className="mt-3 min-h-[72px] text-[13px]"
              data-testid="recent-signal-input"
            />
            <div className="mt-3 flex items-center justify-between">
              <Button
                size="sm"
                onClick={onSaveSignal}
                disabled={signalSaving || signal === (lead.recent_signal || "")}
                data-testid="save-signal-btn"
                className="h-9 bg-[#002FA7] hover:bg-[#002072]"
              >
                <Save className="h-3.5 w-3.5 mr-1.5" />
                {signalSaving ? "Saving…" : "Save signal"}
              </Button>
              {lead.recent_signal && (
                <button
                  onClick={onQualify}
                  disabled={busy === "qualify"}
                  className="text-[12px] font-medium text-[#002FA7] hover:underline"
                  data-testid="regenerate-with-signal-btn"
                >
                  ↺ Regenerate email
                </button>
              )}
            </div>
          </div>

          {/* Contact */}
          <div className="rounded-md border border-slate-200 bg-white p-6">
            <div className="label-caps">Contact</div>
            <dl className="mt-3 space-y-2.5 text-[13px]">
              <div>
                <dt className="text-slate-500">Email</dt>
                <dd className="font-mono text-slate-900 break-all">
                  {lead.email || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Company</dt>
                <dd className="text-slate-900">{lead.company || "—"}</dd>
              </div>
              {lead.company_description && (
                <div>
                  <dt className="text-slate-500">About</dt>
                  <dd className="text-slate-800 leading-relaxed">
                    {lead.company_description}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Pain points */}
          {lead.pain_points?.length > 0 && (
            <div className="rounded-md border border-slate-200 bg-white p-6">
              <div className="label-caps">Pain points</div>
              <ul className="mt-3 space-y-2">
                {lead.pain_points.map((p, i) => (
                  <li
                    key={`pp-${i}-${p.slice(0, 24)}`}
                    className="flex gap-3 text-[13.5px] text-slate-800"
                    data-testid={`pain-point-${i}`}
                  >
                    <span className="font-mono text-[11px] mt-0.5 text-[#002FA7]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Hooks */}
          {lead.hooks?.length > 0 && (
            <div className="rounded-md border border-slate-200 bg-white p-6">
              <div className="label-caps">Hooks</div>
              <div className="mt-3 space-y-3">
                {lead.hooks.map((h, i) => {
                  const isSelected = h === lead.selected_hook;
                  return (
                    <div
                      key={`hook-${i}-${h.slice(0, 24)}`}
                      className={`rounded-md border p-3 text-[13px] ${
                        isSelected
                          ? "border-[#002FA7] bg-blue-50/50"
                          : "border-slate-200 bg-white"
                      }`}
                      data-testid={`hook-${i}`}
                    >
                      {isSelected && (
                        <div className="flex items-center gap-1 label-caps text-[#002FA7] mb-1.5">
                          <CheckCircle2 className="h-3 w-3" /> Selected
                        </div>
                      )}
                      {h}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {lead.reject_reason && lead.status === "rejected" && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-5 text-[13px] text-amber-900">
              <div className="label-caps text-amber-800">Rejected</div>
              <div className="mt-1">{lead.reject_reason}</div>
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-5 space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList
              className="bg-slate-50 border border-slate-200 rounded-md p-1 flex w-full overflow-x-auto no-scrollbar md:w-auto md:inline-flex"
              data-testid="lead-tabs"
            >
              <TabsTrigger
                value="email"
                className="data-[state=active]:bg-white"
                data-testid="tab-email"
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Cold email
              </TabsTrigger>
              <TabsTrigger
                value="followup"
                className="data-[state=active]:bg-white"
                data-testid="tab-followup"
              >
                Follow-up
              </TabsTrigger>
              <TabsTrigger
                value="reply"
                className="data-[state=active]:bg-white"
                data-testid="tab-reply"
              >
                <Inbox className="h-3.5 w-3.5 mr-1.5" /> Reply
              </TabsTrigger>
              <TabsTrigger
                value="linkedin"
                className="data-[state=active]:bg-white"
                data-testid="tab-linkedin"
              >
                <Linkedin className="h-3.5 w-3.5 mr-1.5" /> LinkedIn
              </TabsTrigger>
              <TabsTrigger
                value="objection"
                className="data-[state=active]:bg-white"
                data-testid="tab-objection"
              >
                <MessageSquareWarning className="h-3.5 w-3.5 mr-1.5" />{" "}
                Objection
              </TabsTrigger>
              <TabsTrigger
                value="meeting"
                className="data-[state=active]:bg-white"
                data-testid="tab-meeting"
              >
                <Calendar className="h-3.5 w-3.5 mr-1.5" /> Meeting
              </TabsTrigger>
              <TabsTrigger
                value="voice"
                className="data-[state=active]:bg-white"
                data-testid="tab-voice"
              >
                <Phone className="h-3.5 w-3.5 mr-1.5" /> Voice SDR
              </TabsTrigger>
              <TabsTrigger
                value="sequence"
                className="data-[state=active]:bg-white"
                data-testid="tab-sequence"
              >
                <ListOrdered className="h-3.5 w-3.5 mr-1.5" /> Sequence
              </TabsTrigger>
            </TabsList>

            {/* EMAIL TAB */}
            <TabsContent value="email" className="mt-4">
              <div className="rounded-md border border-slate-200 bg-white">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <div className="label-caps">Cold Email</div>
                    <div className="mt-0.5 font-display font-bold tracking-tight">
                      To: {lead.email || "—"}
                    </div>
                  </div>
                  {lead.sent_at && (
                    <span className="chip text-green-700 border-green-200 bg-green-50">
                      <CheckCircle2 className="h-3 w-3" /> Sent
                    </span>
                  )}
                </div>
                <div className="p-6 space-y-4">
                  {lead.subject_lines?.length > 1 && (
                    <div>
                      <div className="label-caps">Subject options</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {lead.subject_lines.map((s, i) => (
                          <button
                            key={`subj-${i}-${s.slice(0, 32)}`}
                            onClick={() => setSubject(s)}
                            className={`px-3 py-1.5 rounded-md border text-[12.5px] transition-colors ${
                              subject === s
                                ? "border-[#002FA7] text-[#002FA7] bg-blue-50"
                                : "border-slate-200 text-slate-700 hover:border-slate-300"
                            }`}
                            data-testid={`subject-option-${i}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="label-caps">Subject</div>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="mt-2 h-11"
                      data-testid="email-subject-input"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="label-caps">Body</div>
                      {lead.email_language && lead.email_language !== "en" && (
                        <span
                          data-testid="email-language-badge"
                          className="inline-flex items-center gap-1 rounded-full bg-[#002FA7]/10 text-[#002FA7] px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-[#002FA7]" />
                          {lead.email_language}
                        </span>
                      )}
                      {lead.email_language === "en" && (
                        <span
                          data-testid="email-language-badge"
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase"
                        >
                          EN
                        </span>
                      )}
                    </div>
                    <Textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      className="mt-2 min-h-[220px] font-[15px] leading-relaxed"
                      data-testid="email-body-input"
                      placeholder="Generate by running qualification, or write it yourself."
                    />
                    <div className="mt-1 flex items-center justify-between">
                      <span
                        className={`label-caps ${
                          body.trim().split(/\s+/).filter(Boolean).length > 80
                            ? "text-red-600"
                            : ""
                        }`}
                      >
                        {body.trim().split(/\s+/).filter(Boolean).length} words
                        {body.trim().split(/\s+/).filter(Boolean).length > 80 &&
                          " · over 80-word cap"}
                      </span>
                      <span className="label-caps text-slate-400">
                        target 40–70
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    {isLeadDisqualified(lead) ? (
                      <div
                        className="w-full rounded-md border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3"
                        data-testid="disqualified-send-block"
                      >
                        <div className="h-2 w-2 mt-1.5 rounded-full bg-red-500 shrink-0" />
                        <div className="text-[13px] text-red-900 leading-relaxed">
                          <div className="font-semibold">
                            {(lead.lifecycle || lead.status || "").toString().toUpperCase()} — outreach blocked
                          </div>
                          <div className="text-red-800 mt-0.5">
                            {lead.reject_reason
                              ? `Reason: ${lead.reject_reason}`
                              : "This lead is disqualified. Sending is disabled to prevent cross-lead contamination."}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Button
                          onClick={() => {
                            if (!body.trim()) {
                              toast.error("Email body is empty");
                              return;
                            }
                            setPreviewOpen(true);
                          }}
                          disabled={busy === "send" || !body.trim()}
                          data-testid="send-email-btn"
                          className="h-10 bg-[#002FA7] hover:bg-[#002072]"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          {busy === "send" ? "Sending…" : "Send via Resend"}
                        </Button>
                        {config?.test_email_target && (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <Button
                                    variant="outline"
                                    onClick={onSendTest}
                                    disabled={busy === "send-test" || !body.trim()}
                                    data-testid="send-test-btn"
                                    className="h-10 border-slate-200"
                                  >
                                    <Send className="h-4 w-4 mr-2" />
                                    {busy === "send-test"
                                      ? "Sending…"
                                      : `Send test email`}
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                Sends to your configured test inbox
                                {config.test_email_target ? ` (${config.test_email_target})` : ""}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <Button
                          variant="outline"
                          onClick={() => copy(`${subject}\n\n${body}`)}
                          className="h-10 border-slate-200"
                          data-testid="copy-email-btn"
                        >
                          <Copy className="h-4 w-4 mr-2" /> Copy
                        </Button>
                        {lead.final_email && (
                          <Button
                            variant="outline"
                            onClick={onImproveEmail}
                            disabled={busy === "improve-email"}
                            className="h-10 border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5"
                            data-testid="improve-email-btn"
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            {busy === "improve-email"
                              ? "Improving…"
                              : "Improve this email"}
                          </Button>
                        )}
                        {!lead.final_email && (
                          <Button
                            variant="outline"
                            onClick={onQualify}
                            disabled={busy === "qualify"}
                            className="h-10 border-slate-200"
                            data-testid="generate-email-btn"
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate with AI
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                  {config?.sandbox && (
                    <div
                      className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 leading-relaxed"
                      data-testid="sandbox-banner"
                    >
                      <strong>Resend sandbox mode</strong> — sending as{" "}
                      <span className="font-mono">
                        {config.sender_name
                          ? `${config.sender_name} <${config.sender_email}>`
                          : config.sender_email}
                      </span>{" "}
                      with <strong>Reply-To</strong>{" "}
                      <span className="font-mono">{config.reply_to}</span>. Real
                      prospect sends need a verified domain at
                      resend.com/domains.
                    </div>
                  )}

                  {emailImprovement && (
                    <EmailImprovementDiff
                      original={emailImprovement.original_email}
                      improved={emailImprovement.improved_email}
                      improvements={emailImprovement.improvements}
                      onAccept={onAcceptImprovement}
                      onDismiss={() => setEmailImprovement(null)}
                    />
                  )}
                </div>
              </div>

              {/* AI SALES BRAIN — premium insight card */}
              <SalesBrainCard
                lead={lead}
                brain={brain}
                busy={busy === "brain"}
                onExplain={onExplainLead}
                onRegenerateLanguage={onRegenerateLanguage}
                regenBusy={busy === "regen-lang"}
                disqualified={isLeadDisqualified(lead)}
              />
            </TabsContent>

            {/* FOLLOWUP TAB */}
            <TabsContent value="followup" className="mt-4">
              <div className="rounded-md border border-slate-200 bg-white p-6 space-y-4">
                <div>
                  <div className="label-caps">Follow-up email</div>
                  <p className="mt-1 text-[13px] text-slate-600">
                    Adds a new angle, under 60 words, very different from the
                    first.
                  </p>
                </div>
                <Button
                  onClick={onFollowup}
                  disabled={busy === "followup" || !lead.final_email}
                  data-testid="generate-followup-btn"
                  className="bg-[#002FA7] hover:bg-[#002072] h-10"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {getRegenLabel(
                    busy === "followup",
                    "Generating…",
                    followupBody,
                    "Regenerate follow-up",
                    "Generate follow-up",
                  )}
                </Button>
                {!lead.final_email && (
                  <p className="text-[12.5px] text-amber-700">
                    Generate the initial email first.
                  </p>
                )}
                {followupBody && (
                  <>
                    <Textarea
                      value={followupBody}
                      onChange={(e) => setFollowupBody(e.target.value)}
                      className="min-h-[160px]"
                      data-testid="followup-body"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={onSendFollowup}
                        disabled={busy === "send-followup" || isLeadDisqualified(lead)}
                        data-testid="send-followup-btn"
                        className="bg-[#002FA7] hover:bg-[#002072] h-10"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {busy === "send-followup"
                          ? "Sending…"
                          : "Send follow-up"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => copy(followupBody)}
                        className="h-10 border-slate-200"
                        data-testid="copy-followup-btn"
                      >
                        <Copy className="h-4 w-4 mr-2" /> Copy
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* LINKEDIN TAB */}
            <TabsContent value="linkedin" className="mt-4">
              <div className="rounded-md border border-slate-200 bg-white p-6 space-y-4">
                <div>
                  <div className="label-caps">LinkedIn DM</div>
                  <p className="mt-1 text-[13px] text-slate-600">
                    Max 40 words. Casual. No selling. Conversation starter.
                  </p>
                </div>
                <Button
                  onClick={onLinkedin}
                  disabled={busy === "linkedin"}
                  data-testid="generate-linkedin-btn"
                  className="bg-[#002FA7] hover:bg-[#002072] h-10"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {getRegenLabel(
                    busy === "linkedin",
                    "Generating…",
                    lead.linkedin_dm,
                    "Regenerate DM",
                    "Generate DM",
                  )}
                </Button>
                {lead.linkedin_dm && (
                  <>
                    <div
                      className="rounded-md border border-slate-200 bg-slate-50 p-4 text-[14px] leading-relaxed whitespace-pre-wrap"
                      data-testid="linkedin-dm-output"
                    >
                      {lead.linkedin_dm}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => copy(lead.linkedin_dm)}
                      className="h-9 border-slate-200"
                      data-testid="copy-linkedin-btn"
                    >
                      <Copy className="h-4 w-4 mr-2" /> Copy DM
                    </Button>
                  </>
                )}
              </div>
            </TabsContent>

            {/* OBJECTION TAB */}
            <TabsContent value="objection" className="mt-4">
              <div className="rounded-md border border-slate-200 bg-white p-6 space-y-4">
                <div>
                  <div className="label-caps">Objection handler</div>
                  <p className="mt-1 text-[13px] text-slate-600">
                    Paste the prospect's objection. Get a 50-word reply that
                    reframes and moves toward a demo.
                  </p>
                </div>
                <Textarea
                  value={objection}
                  onChange={(e) => setObjection(e.target.value)}
                  placeholder="e.g. Not a priority right now, too expensive, we built it in-house…"
                  className="min-h-[100px]"
                  data-testid="objection-input"
                />
                <Button
                  onClick={onObjection}
                  disabled={busy === "objection" || !objection.trim()}
                  data-testid="generate-objection-btn"
                  className="bg-[#002FA7] hover:bg-[#002072] h-10"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {busy === "objection" ? "Thinking…" : "Generate reply"}
                </Button>
                {lead.last_objection_reply && (
                  <div>
                    <div className="label-caps">Suggested reply</div>
                    <div
                      className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-4 text-[14px] leading-relaxed whitespace-pre-wrap"
                      data-testid="objection-reply-output"
                    >
                      {lead.last_objection_reply}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => copy(lead.last_objection_reply)}
                      className="mt-3 h-9 border-slate-200"
                      data-testid="copy-objection-btn"
                    >
                      <Copy className="h-4 w-4 mr-2" /> Copy
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* REPLY TAB (Smart Reply Assistant) */}
            <TabsContent value="reply" className="mt-4">
              <div className="rounded-md border border-slate-200 bg-white p-6 space-y-4">
                <div>
                  <div className="label-caps">Smart Reply Assistant</div>
                  <p className="mt-1 text-[13px] text-slate-600">
                    Paste what the prospect replied. AI detects intent and
                    drafts the best response to move toward a meeting.
                  </p>
                </div>
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Paste the prospect's reply here…"
                  className="min-h-[120px]"
                  data-testid="smart-reply-input"
                />
                <Button
                  onClick={onSmartReply}
                  disabled={busy === "smart-reply" || !replyText.trim()}
                  data-testid="generate-smart-reply-btn"
                  className="bg-[#002FA7] hover:bg-[#002072] h-10"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {busy === "smart-reply"
                    ? "Analyzing…"
                    : "Analyze & draft reply"}
                </Button>

                {lead.last_reply_intent && (
                  <>
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                      <span
                        className="chip"
                        data-testid="reply-intent-chip"
                        style={getSentimentStyle(lead.last_reply_sentiment)}
                      >
                        <Zap className="h-3 w-3" />
                        Intent: {lead.last_reply_intent}
                      </span>
                      <span className="chip">
                        Sentiment: {lead.last_reply_sentiment}
                      </span>
                      <span className="chip text-[#002FA7] border-blue-200 bg-blue-50">
                        Next: {lead.last_reply_suggested_action}
                      </span>
                    </div>

                    <div>
                      <div className="label-caps">Draft reply</div>
                      <Textarea
                        value={lead.last_reply_draft || ""}
                        readOnly
                        className="mt-2 min-h-[140px] bg-slate-50 text-[14px] leading-relaxed"
                        data-testid="smart-reply-draft"
                      />
                      <div className="mt-1 label-caps">
                        {(lead.last_reply_draft || "")
                          .trim()
                          .split(/\s+/)
                          .filter(Boolean).length}{" "}
                        words
                      </div>
                    </div>

                    {/* Deal Accelerator suggestion — framework-driven response.
                        Surfaces only when backend populated lead.accelerator. */}
                    <AcceleratorSuggestion
                      accelerator={lead.accelerator}
                      onUseReply={(text) => {
                        setBody(text);
                        // Nudge user to the Email tab so they can review+send.
                        setActiveTab("email");
                        toast.success("Suggested reply copied to composer", {
                          description: "Review on the Email tab before sending.",
                        });
                      }}
                    />

                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        onClick={() => copy(lead.last_reply_draft || "")}
                        className="h-9 border-slate-200"
                        data-testid="copy-smart-reply-btn"
                      >
                        <Copy className="h-4 w-4 mr-2" /> Copy draft
                      </Button>
                      {lead.last_reply_suggested_action === "BOOK_MEETING" && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            document
                              .querySelector('[data-testid="tab-meeting"]')
                              ?.click();
                          }}
                          className="h-9 border-slate-200"
                          data-testid="go-to-meeting-tab-btn"
                        >
                          <Calendar className="h-4 w-4 mr-2" /> Book meeting
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* MEETING TAB */}
            <TabsContent value="meeting" className="mt-4">
              <div className="rounded-md border border-slate-200 bg-white p-6 space-y-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="label-caps">High-conversion meeting engine</div>
                    <p className="mt-1 text-[13px] text-slate-600">
                      Two variations. A — direct & simple. B — curiosity-driven.
                      Calendar link injected automatically if set in Setup.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="label-caps">Slot 1 (fallback)</div>
                    <Input
                      value={slot1}
                      onChange={(e) => setSlot1(e.target.value)}
                      className="mt-2 h-10"
                      data-testid="meeting-slot-1"
                      placeholder="Tue 11:00 AM"
                    />
                  </div>
                  <div>
                    <div className="label-caps">Slot 2 (fallback)</div>
                    <Input
                      value={slot2}
                      onChange={(e) => setSlot2(e.target.value)}
                      className="mt-2 h-10"
                      data-testid="meeting-slot-2"
                      placeholder="Thu 3:30 PM"
                    />
                  </div>
                </div>

                <Button
                  onClick={onMeeting}
                  disabled={busy === "meeting"}
                  data-testid="generate-meeting-btn"
                  className="bg-[#002FA7] hover:bg-[#002072] h-10"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {getRegenLabel(
                    busy === "meeting",
                    "Generating…",
                    lead.meeting_variation_a,
                    "Regenerate 2 variations",
                    "Generate 2 variations",
                  )}
                </Button>

                {(lead.meeting_variation_a || lead.meeting_variation_b) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    {["a", "b"].map((v) => {
                      const msg =
                        v === "a"
                          ? lead.meeting_variation_a
                          : lead.meeting_variation_b;
                      if (!msg) return null;
                      const label =
                        v === "a"
                          ? "Variation A — Direct"
                          : "Variation B — Curiosity";
                      return (
                        <div
                          key={v}
                          className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3"
                          data-testid={`meeting-variation-${v}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="label-caps text-[#002FA7]">
                              {label}
                            </span>
                            <span className="label-caps text-slate-400">
                              {msg.trim().split(/\s+/).filter(Boolean).length}w
                            </span>
                          </div>
                          <div className="text-[14px] leading-relaxed whitespace-pre-wrap text-slate-900">
                            {msg}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copy(msg)}
                            className="h-8 border-slate-200"
                            data-testid={`copy-meeting-${v}-btn`}
                          >
                            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Confirmation / No-show reduction */}
                <div className="pt-5 mt-2 border-t border-slate-100 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="label-caps flex items-center gap-1.5">
                        <CheckCircle2 className="h-3 w-3" /> After booking —
                        no-show reducer
                      </div>
                      <p className="mt-1 text-[12.5px] text-slate-600">
                        Short confirmation (&lt;40 words). Reinforces value, sets
                        expectation.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={onConfirmation}
                      disabled={busy === "confirmation"}
                      data-testid="generate-confirmation-btn"
                      className="h-9 border-slate-200"
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                      {getRegenLabel(
                        busy === "confirmation",
                        "Generating…",
                        lead.confirmation_message,
                        "Regenerate",
                        "Generate confirmation",
                      )}
                    </Button>
                  </div>
                  {lead.confirmation_message && (
                    <div
                      className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-[14px] leading-relaxed whitespace-pre-wrap text-slate-900"
                      data-testid="confirmation-output"
                    >
                      {lead.confirmation_message}
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copy(lead.confirmation_message)}
                          className="h-8 border-emerald-300 bg-white"
                          data-testid="copy-confirmation-btn"
                        >
                          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* VOICE SDR TAB */}
            <TabsContent value="voice" className="mt-4">
              <VoiceSdrPanel
                lead={lead}
                voiceConfig={voiceConfig}
                busy={busy === "call"}
                simPhase={simPhase}
                onCall={onCallLead}
                onRefresh={load}
                disqualified={isLeadDisqualified(lead)}
                callError={callError}
                onDismissError={() => setCallError(null)}
              />
            </TabsContent>

            {/* SEQUENCE TAB (Phase 2 — Multi-Touch Sequence Builder) */}
            <TabsContent value="sequence" className="mt-4">
              <SequenceTab
                lead={lead}
                sequence={sequence}
                busy={busy === "sequence"}
                disqualified={isLeadDisqualified(lead)}
                onGenerate={onGenerateSequence}
              />
            </TabsContent>

          </Tabs>
        </div>
      </div>

      {/* Pre-send email preview — read-only check before firing via Resend.
          Boosts user confidence; no scoring, no AI, one-click Send. */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          className="max-w-2xl"
          data-testid="email-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle>Preview email before sending</DialogTitle>
            <DialogDescription>
              Review the message below. This is exactly what{" "}
              <span className="font-medium text-slate-800">
                {lead.email || "the recipient"}
              </span>{" "}
              will receive.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/60 p-4">
            <div>
              <div className="label-caps text-[10px] text-slate-500">
                Subject
              </div>
              <div
                className="mt-1 font-semibold text-slate-900"
                data-testid="email-preview-subject"
              >
                {subject || "(no subject)"}
              </div>
            </div>
            <div className="border-t border-slate-200 pt-3">
              <div className="label-caps text-[10px] text-slate-500">Body</div>
              <div
                className="mt-1 whitespace-pre-wrap text-[13px] text-slate-800 leading-relaxed max-h-[52vh] overflow-y-auto"
                data-testid="email-preview-body"
              >
                {body}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              className="h-10 border-slate-200"
              data-testid="email-preview-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setPreviewOpen(false);
                onSend();
              }}
              disabled={busy === "send" || !body.trim()}
              className="h-10 bg-[#002FA7] hover:bg-[#002072]"
              data-testid="email-preview-send"
            >
              <Send className="h-4 w-4 mr-2" />
              {busy === "send" ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Per-Lead Autonomous "ON" confirmation — high-stakes guard. */}
      <AlertDialog
        open={pendingAutoMode === "on"}
        onOpenChange={(open) => !open && setPendingAutoMode(null)}
      >
        <AlertDialogContent data-testid="autonomous-on-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Enable autonomous AI for this lead?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The AI will execute emails and calls on this lead automatically —
              without waiting for your approval. This overrides your workspace
              execution mode. You can switch back to <b>Inherit</b> or{" "}
              <b>Off</b> anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="autonomous-on-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="autonomous-on-confirm"
              onClick={() => {
                const next = pendingAutoMode;
                setPendingAutoMode(null);
                applyAutonomousMode(next);
              }}
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
            >
              Yes, run autonomously
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const LIFECYCLE_STEPS = [
  "NEW",
  "QUALIFIED",
  "EMAILED",
  "FOLLOWUP_PENDING",
  "REPLIED",
  "MEETING_BOOKED",
];

const CHANNEL_ICONS = {
  Email: Mail,
  LinkedIn: Linkedin,
  "Follow-up": Reply,
  Call: Phone,
};

function StateMachineStrip({ lead }) {
  const lc = lead.lifecycle || "NEW";
  const rejected = lc === "REJECTED" || lc === "INVALID";
  const activeIdx = rejected ? -1 : LIFECYCLE_STEPS.indexOf(lc);
  const ChannelIcon = CHANNEL_ICONS[lead.recommended_channel] || Mail;

  return (
    <div
      className="rounded-md border border-slate-200 bg-white p-5"
      data-testid="state-machine-strip"
    >
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center flex-wrap gap-x-1 gap-y-2">
          {LIFECYCLE_STEPS.map((step, i) => {
            const isActive = i === activeIdx;
            const isPast = activeIdx !== -1 && i < activeIdx;
            return (
              <span
                key={step}
                className="flex items-center gap-1"
                data-testid={`lifecycle-step-${step}`}
              >
                <span
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${getLifecycleStepClass(
                    isActive,
                    isPast,
                  )}`}
                >
                  {step.replace("_", " ")}
                </span>
                {i < LIFECYCLE_STEPS.length - 1 && (
                  <span className="text-slate-300 text-[10px]">›</span>
                )}
              </span>
            );
          })}
          {rejected && (
            <span className="ml-2 px-2.5 py-1 rounded-md text-[11px] font-semibold border bg-red-50 text-red-700 border-red-200">
              {lc}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {lead.next_action && (
            <div className="text-right">
              <div className="label-caps text-slate-500">Next action</div>
              <div
                className="font-display font-bold text-[13px] text-[#002FA7]"
                data-testid="next-action-pill"
              >
                {lead.next_action.replace("_", " ")}
              </div>
            </div>
          )}
          {lead.recommended_channel && (
            <div className="h-10 w-px bg-slate-200" />
          )}
          {lead.recommended_channel && (
            <div className="text-right max-w-[280px]">
              <div className="label-caps text-slate-500 flex items-center justify-end gap-1">
                <ChannelIcon className="h-3 w-3" /> Channel
              </div>
              <div
                className="font-display font-bold text-[13px] text-slate-900"
                data-testid="recommended-channel"
              >
                {lead.recommended_channel}
              </div>
              {lead.channel_reason && (
                <div className="text-[11.5px] text-slate-500 leading-snug">
                  {lead.channel_reason}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



function SalesBrainCard({
  lead,
  brain,
  busy,
  onExplain,
  onRegenerateLanguage,
  regenBusy,
  disqualified,
}) {
  const currentLang = lead?.email_language || "en";
  return (
    <section
      className="relative rounded-2xl border border-[#B8C9FF] bg-gradient-to-br from-[#F0F5FF] via-[#F6F9FF] to-white p-6 shadow-[0_1px_2px_rgba(0,47,167,0.04)]"
      data-testid="sales-brain-card"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-[#002FA7] text-white grid place-items-center shrink-0 shadow-sm">
            <Brain className="h-4 w-4" strokeWidth={2.25} />
          </div>
          <div>
            <div className="font-display text-[17px] font-bold tracking-tight text-slate-900">
              AI Sales Brain
            </div>
            <div className="text-[12px] text-slate-500 leading-relaxed">
              Understands your leads and guides next actions.
            </div>
          </div>
        </div>

        {/* Per-lead language override (disabled while busy or disqualified) */}
        {!disqualified && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Languages className="h-3.5 w-3.5 text-slate-400" />
            <Select
              value={currentLang}
              onValueChange={onRegenerateLanguage}
              disabled={regenBusy}
            >
              <SelectTrigger
                className="h-8 w-[150px] text-[12px]"
                data-testid="brain-language-select"
              >
                <SelectValue placeholder="Regenerate in…" />
              </SelectTrigger>
              <SelectContent>
                {BRAIN_LANGUAGE_OPTIONS.map((l) => (
                  <SelectItem
                    key={l.code}
                    value={l.code}
                    data-testid={`brain-lang-${l.code}`}
                  >
                    {regenBusy && l.code !== currentLang
                      ? "Regenerating…"
                      : l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="mt-5">
        {!brain && !busy && (
          <div
            className="rounded-xl border border-dashed border-[#B8C9FF] bg-white/70 px-5 py-6 text-center"
            data-testid="brain-placeholder"
          >
            <p className="text-[13px] text-slate-600 leading-relaxed">
              Click to understand this lead and next action.
            </p>
            <Button
              onClick={onExplain}
              className="mt-3 h-9 rounded-full bg-[#002FA7] hover:bg-[#002072] px-5 text-[13px]"
              data-testid="explain-lead-btn"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Explain this lead
            </Button>
          </div>
        )}

        {busy && (
          <div
            className="rounded-xl border border-[#B8C9FF] bg-white/70 px-5 py-6 flex items-center gap-3"
            data-testid="brain-loading"
          >
            <div className="h-4 w-4 rounded-full border-2 border-[#002FA7] border-t-transparent animate-spin" />
            <p className="text-[13px] text-slate-600 italic">
              Analyzing lead…
            </p>
          </div>
        )}

        {brain && !busy && (
          <div
            className="animate-brain-fade-in"
            data-testid="brain-result"
          >
            <div
              className="rounded-xl border border-white/80 bg-white/90 px-5 py-4 shadow-[0_1px_2px_rgba(0,47,167,0.04)]"
              data-testid="brain-explanation"
            >
              <div className="label-caps !text-[10px] text-[#002FA7]">
                Why this lead
              </div>
              <p className="mt-2 text-[14px] text-slate-800 leading-relaxed whitespace-pre-line">
                {brain.explanation}
              </p>
            </div>

            {brain.intent_label && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="label-caps !text-[10px] text-slate-500">
                  Deal intent
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12.5px] font-semibold ${getIntentClass(
                    brain.intent_label,
                  )}`}
                  data-testid="brain-intent-chip"
                >
                  <Flame className="h-3 w-3" />
                  {brain.intent_label}
                </span>
                {brain.intent_reason && (
                  <span
                    className="text-[11.5px] text-slate-500 leading-snug"
                    data-testid="brain-intent-reason"
                  >
                    · {brain.intent_reason}
                  </span>
                )}
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="label-caps !text-[10px] text-slate-500">
                  Recommended action
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12.5px] font-semibold ${getActionToneClass(
                    brain.action_tone,
                  )}`}
                  data-testid="brain-action-chip"
                >
                  {brain.action}
                </span>
                {brain.action_timing && (
                  <span
                    className="text-[11.5px] text-slate-500 font-medium"
                    data-testid="brain-action-timing"
                  >
                    · {brain.action_timing}
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onExplain}
                className="h-8 text-[12px] text-slate-500 hover:text-[#002FA7] ml-auto"
                data-testid="refresh-brain-btn"
              >
                <RefreshCw className="h-3 w-3 mr-1.5" />
                Refresh suggestion
              </Button>
            </div>
            {brain.action_reason && (
              <p
                className="mt-2 text-[12px] text-slate-500 leading-relaxed"
                data-testid="brain-action-reason"
              >
                {brain.action_reason}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ============ VOICE SDR PANEL ============

const OUTCOME_CHIP_CLASS = {
  "Interested": "bg-green-50 text-green-800 border-green-200",
  "Not Interested": "bg-red-50 text-red-700 border-red-200",
  "Busy - Rescheduled": "bg-amber-50 text-amber-800 border-amber-200",
  "No Answer": "bg-slate-100 text-slate-700 border-slate-200",
};
const getOutcomeClass = (outcome) =>
  OUTCOME_CHIP_CLASS[outcome] || OUTCOME_CHIP_CLASS["Interested"];

const SIM_PHASE_COPY = {
  calling: "Calling…",
  connected: "Connected",
  talking: "Conversation in progress…",
};

function VoiceSdrPanel({
  lead,
  voiceConfig,
  busy,
  simPhase,
  onCall,
  disqualified,
  callError,
  onDismissError,
}) {
  const isDemo = voiceConfig?.mode !== "live";
  const keyPresentNoNumber =
    isDemo &&
    voiceConfig?.vapi_key_present &&
    !voiceConfig?.vapi_phone_number_id_present;
  const hasCall = Boolean(lead.call_transcript && lead.call_transcript.length > 0);

  return (
    <div className="space-y-4">
      {/* Control row */}
      <div className="rounded-md border border-slate-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#002FA7] text-white grid place-items-center shrink-0 shadow-sm">
              <Phone className="h-4.5 w-4.5" strokeWidth={2.25} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-display text-[16px] font-bold tracking-tight text-slate-900">
                  AI Voice SDR
                </div>
                {isDemo && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase"
                    data-testid="voice-demo-badge"
                  >
                    Demo Mode
                  </span>
                )}
              </div>
              <div className="text-[12.5px] text-slate-500 leading-relaxed">
                Real-time outbound AI calling with conversation handling and
                live summary.
              </div>
              {keyPresentNoNumber && (
                <div
                  className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900 leading-relaxed"
                  data-testid="vapi-setup-hint"
                >
                  <strong>VAPI key detected.</strong> Add a{" "}
                  <span className="font-mono">VAPI_PHONE_NUMBER_ID</span> in the
                  backend <span className="font-mono">.env</span> to enable live
                  calling. Provision a number at{" "}
                  <a
                    href="https://dashboard.vapi.ai/phone-numbers"
                    target="_blank"
                    rel="noreferrer"
                    className="underline font-medium"
                  >
                    dashboard.vapi.ai/phone-numbers
                  </a>
                  .
                </div>
              )}
              {lead.phone ? (
                <div className="mt-2 text-[12px] font-mono text-slate-700">
                  {lead.phone}
                </div>
              ) : (
                <div className="mt-2 text-[12px] text-slate-500 italic">
                  {isDemo
                    ? "No phone on file — demo mode will simulate regardless."
                    : "Add a phone number to this lead before calling."}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <Button
              onClick={() => onCall(false)}
              disabled={busy || disqualified}
              data-testid="call-lead-btn"
              className="h-10 rounded-full bg-[#002FA7] hover:bg-[#002072] px-5"
            >
              <Phone className="h-4 w-4 mr-2" />
              {busy ? (SIM_PHASE_COPY[simPhase] || "Calling…") : "Call via AI"}
            </Button>
            {disqualified && (
              <div className="text-[11px] text-red-700">
                Disqualified leads cannot be called.
              </div>
            )}
          </div>
        </div>

        {/* Live call state strip */}
        {busy && (
          <div
            className="mt-5 rounded-xl border border-[#B8C9FF] bg-[#F0F5FF] px-5 py-4 flex items-center gap-4"
            data-testid="call-progress"
          >
            <div className="relative flex items-center justify-center">
              <div className="h-3 w-3 rounded-full bg-[#002FA7]" />
              <div className="absolute h-3 w-3 rounded-full bg-[#002FA7] animate-ping opacity-75" />
            </div>
            <div>
              <div className="text-[13px] font-semibold text-[#002FA7]">
                {SIM_PHASE_COPY[simPhase] || "Calling…"}
              </div>
              <div className="text-[11.5px] text-slate-500">
                {simPhase === "calling" && "Dialing the prospect…"}
                {simPhase === "connected" && "Prospect picked up."}
                {simPhase === "talking" && "AI SDR having a natural conversation."}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Live VAPI error + Simulate fallback (demo-resilience only) */}
      {callError && !busy && (
        <div
          className="rounded-md border border-red-200 bg-red-50 px-5 py-4 animate-brain-fade-in"
          data-testid="call-error-card"
        >
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-lg bg-red-100 text-red-700 grid place-items-center shrink-0">
              <Phone className="h-4 w-4" strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-caps !text-[10px] text-red-700">
                Live call failed
              </div>
              <p
                className="mt-1 text-[13px] text-slate-800 leading-relaxed"
                data-testid="call-error-message"
              >
                {callError.message}
              </p>
              {callError.canSimulate && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={() => onCall(true)}
                    size="sm"
                    variant="outline"
                    className="h-8 text-[12px] border-[#B8C9FF] bg-white text-[#002FA7] hover:bg-[#F0F5FF]"
                    data-testid="simulate-call-btn"
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                    Simulate Call
                  </Button>
                  <span className="text-[11px] text-slate-500">
                    (Demo mode — synthetic transcript + summary)
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={onDismissError}
              className="text-slate-400 hover:text-slate-600 shrink-0"
              aria-label="Dismiss"
              data-testid="call-error-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Call summary + transcript (only after a call) */}
      {hasCall && !busy && (
        <div
          className="rounded-md border border-slate-200 bg-white animate-brain-fade-in"
          data-testid="call-summary-card"
        >
          <div className="border-b border-slate-100 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="label-caps !text-[10.5px] !tracking-[0.2em] text-[#002FA7]">
                Call Summary
              </div>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold ${getOutcomeClass(
                  lead.call_outcome,
                )}`}
                data-testid="call-outcome-chip"
              >
                {lead.call_outcome}
              </span>
              {lead.call_mode === "simulated" && (
                <span
                  className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400"
                  data-testid="call-simulated-label"
                >
                  {lead.call_is_fallback
                    ? "· Simulated Call (Demo Mode)"
                    : "· simulated"}
                </span>
              )}
            </div>
            {lead.call_ended_at && (
              <div className="text-[11px] text-slate-400">
                Ended {new Date(lead.call_ended_at).toLocaleTimeString()}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-x divide-slate-100">
            {/* Key points + next step */}
            <div className="p-6 space-y-4">
              {(lead.call_key_points || []).length > 0 && (
                <div>
                  <div className="label-caps !text-[10px] text-slate-500 mb-2">
                    Key points discussed
                  </div>
                  <ul
                    className="space-y-1.5"
                    data-testid="call-key-points"
                  >
                    {(lead.call_key_points || []).map((p, i) => (
                      <li
                        key={`kp-${i}-${(p || "").slice(0, 40)}`}
                        className="text-[13px] text-slate-800 leading-relaxed flex gap-2"
                      >
                        <span className="text-[#002FA7] shrink-0">•</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(lead.call_objections || []).length > 0 && (
                <div>
                  <div className="label-caps !text-[10px] text-slate-500 mb-2">
                    Objections raised
                  </div>
                  <ul
                    className="space-y-1.5"
                    data-testid="call-objections"
                  >
                    {(lead.call_objections || []).map((o, i) => (
                      <li
                        key={`obj-${i}-${(o || "").slice(0, 40)}`}
                        className="text-[13px] text-slate-700 leading-relaxed flex gap-2"
                      >
                        <span className="text-amber-600 shrink-0">⚠</span>
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {lead.call_next_step && (
                <div className="rounded-md border border-[#B8C9FF] bg-[#F0F5FF] px-4 py-3">
                  <div className="label-caps !text-[10px] text-[#002FA7] mb-1">
                    Recommended next step
                  </div>
                  <div
                    className="text-[13px] font-semibold text-slate-900 leading-relaxed"
                    data-testid="call-next-step"
                  >
                    {lead.call_next_step}
                  </div>
                </div>
              )}
            </div>

            {/* Transcript */}
            <div className="p-6">
              <div className="label-caps !text-[10px] text-slate-500 mb-2">
                Transcript
              </div>
              <div
                className="space-y-2 max-h-[340px] overflow-y-auto pr-2"
                data-testid="call-transcript"
              >
                {(lead.call_transcript || []).map((turn, i) => {
                  const isSdr = (turn.speaker || "").toLowerCase() === "sdr";
                  return (
                    <div
                      key={`turn-${i}-${(turn.text || "").slice(0, 40)}`}
                      className={`rounded-lg px-3 py-2 text-[12.5px] leading-relaxed ${
                        isSdr
                          ? "bg-[#F0F5FF] text-slate-900 border border-[#B8C9FF]"
                          : "bg-slate-50 text-slate-800 border border-slate-200"
                      }`}
                    >
                      <div
                        className={`text-[10px] font-semibold tracking-wider uppercase mb-0.5 ${
                          isSdr ? "text-[#002FA7]" : "text-slate-500"
                        }`}
                      >
                        {isSdr ? "AI SDR" : "Prospect"}
                      </div>
                      <div>{turn.text}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {!hasCall && !busy && (
        <div
          className="rounded-md border border-dashed border-slate-200 bg-white px-6 py-8 text-center"
          data-testid="no-call-placeholder"
        >
          <Phone className="h-6 w-6 text-slate-300 mx-auto mb-2" />
          <p className="text-[13px] text-slate-500 leading-relaxed">
            No calls yet. Click{" "}
            <span className="font-semibold text-slate-700">“Call via AI”</span>{" "}
            to start an AI-driven discovery call.
          </p>
          {isDemo && (
            <p className="text-[11.5px] text-slate-400 mt-1.5">
              Running in demo mode — one of 3 realistic scenarios will be
              simulated (interested / not interested / busy).
            </p>
          )}
        </div>
      )}
    </div>
  );
}


function EmailImprovementDiff({ original, improved, improvements, onAccept, onDismiss }) {
  return (
    <div
      className="rounded-xl border border-[#B8C9FF] bg-gradient-to-br from-[#F0F5FF] via-[#F6F9FF] to-white p-5 animate-brain-fade-in"
      data-testid="email-improvement-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-3.5 w-3.5 text-[#002FA7]" />
        <div className="label-caps !text-[10px] !tracking-[0.2em] text-[#002FA7]">
          Improved email
        </div>
      </div>
      {improvements && improvements.length > 0 && (
        <ul className="mt-2 mb-4 space-y-1">
          {improvements.map((imp, i) => (
            <li
              key={`imp-${i}-${(imp || "").slice(0, 40)}`}
              className="text-[12.5px] text-slate-700 leading-relaxed flex gap-2"
              data-testid={`improvement-bullet-${i}`}
            >
              <span className="text-[#002FA7] shrink-0">✓</span>
              <span>{imp}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div
          className="rounded-md border border-slate-200 bg-white p-3"
          data-testid="diff-original"
        >
          <div className="label-caps !text-[10px] text-slate-500 mb-1.5">
            Original
          </div>
          <p className="text-[12.5px] text-slate-700 leading-relaxed whitespace-pre-line">
            {original}
          </p>
        </div>
        <div
          className="rounded-md border border-[#002FA7]/30 bg-white p-3 ring-1 ring-[#002FA7]/20"
          data-testid="diff-improved"
        >
          <div className="label-caps !text-[10px] text-[#002FA7] mb-1.5">
            Improved
          </div>
          <p className="text-[12.5px] text-slate-900 leading-relaxed whitespace-pre-line">
            {improved}
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          onClick={onAccept}
          className="h-8 bg-[#002FA7] hover:bg-[#002072] text-[12px]"
          data-testid="accept-improvement-btn"
        >
          Use this version
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="h-8 text-[12px] text-slate-500"
          data-testid="dismiss-improvement-btn"
        >
          Keep original
        </Button>
      </div>
    </div>
  );
}


// ============ MULTI-TOUCH SEQUENCE BUILDER TAB (Phase 2 — Prompt 3) ============

const CHANNEL_BADGE = {
  Email: {
    cls: "bg-blue-50 text-[#002FA7] border-blue-200",
    Icon: Mail,
  },
  LinkedIn: {
    cls: "bg-sky-50 text-sky-700 border-sky-200",
    Icon: Linkedin,
  },
};

function SequenceTab({ lead, sequence, busy, disqualified, onGenerate }) {
  const steps = sequence?.steps || [];

  if (disqualified) {
    return (
      <div
        className="rounded-md border border-slate-200 bg-white p-8 text-center"
        data-testid="sequence-disqualified"
      >
        <p className="text-[13px] text-slate-500">
          Sequences are disabled for disqualified leads.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
        <div>
          <div className="label-caps">Multi-Touch Sequence</div>
          <div className="mt-0.5 font-display font-bold tracking-tight text-slate-900">
            5-Step Outbound Plan
          </div>
          <p className="mt-1 text-[12.5px] text-slate-500 leading-relaxed">
            Each step uses a different angle — problem intro, new insight,
            social proof, LinkedIn, final nudge. Same voice, no repetition.
          </p>
        </div>
        <Button
          onClick={onGenerate}
          disabled={busy}
          size="sm"
          className="h-9 bg-[#002FA7] hover:bg-[#002072] text-[12.5px] shrink-0"
          data-testid="generate-sequence-btn"
        >
          {busy ? (
            <>
              <div className="h-3 w-3 mr-2 rounded-full border-2 border-white/50 border-t-white animate-spin" />
              Generating…
            </>
          ) : (
            <>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              {steps.length ? "Regenerate" : "Generate sequence"}
            </>
          )}
        </Button>
      </div>

      {!steps.length && !busy && (
        <div
          className="px-6 py-10 text-center"
          data-testid="sequence-placeholder"
        >
          <ListOrdered className="h-8 w-8 mx-auto text-slate-300" />
          <p className="mt-2 text-[13px] text-slate-500">
            No sequence yet for {lead?.name || "this lead"}.
          </p>
          <p className="text-[12px] text-slate-400">
            Generate a 5-step plan tailored to this lead's role, company, and pain.
          </p>
        </div>
      )}

      {busy && !steps.length && (
        <div className="px-6 py-10 flex items-center justify-center gap-3">
          <div className="h-4 w-4 rounded-full border-2 border-[#002FA7] border-t-transparent animate-spin" />
          <p className="text-[13px] text-slate-600 italic">
            Designing 5-step sequence…
          </p>
        </div>
      )}

      {steps.length > 0 && (
        <ol
          className="p-6 space-y-3"
          data-testid="sequence-steps"
        >
          {steps.map((s) => {
            const meta =
              CHANNEL_BADGE[s.channel] || CHANNEL_BADGE.Email;
            const ChannelIcon = meta.Icon;
            const waitText =
              s.wait_days === 0
                ? "Send now"
                : `Wait ${s.wait_days} day${s.wait_days > 1 ? "s" : ""}`;
            return (
              <li
                key={s.step}
                className="rounded-lg border border-slate-200 bg-white hover:border-[#B8C9FF] transition-colors"
                data-testid={`sequence-step-${s.step}`}
              >
                <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-100 bg-slate-50/50">
                  <span className="h-6 w-6 rounded-full bg-[#002FA7] text-white text-[11px] font-bold grid place-items-center shrink-0">
                    {s.step}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${meta.cls}`}
                  >
                    <ChannelIcon className="h-3 w-3" />
                    {s.channel}
                  </span>
                  <span className="text-[12px] font-semibold text-slate-800">
                    {s.angle_label}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-500">
                    <Clock className="h-3 w-3" />
                    {waitText}
                  </span>
                </div>
                <div className="px-4 py-3">
                  {s.subject ? (
                    <div className="mb-2 text-[12px] text-slate-500">
                      <span className="label-caps !text-[9.5px]">
                        Subject
                      </span>{" "}
                      <span className="text-slate-800 font-medium">
                        {s.subject}
                      </span>
                    </div>
                  ) : null}
                  <p
                    className="text-[13.5px] text-slate-800 leading-relaxed whitespace-pre-line"
                    data-testid={`sequence-message-${s.step}`}
                  >
                    {s.message}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}


// ============ Closing Layer (Tasks 1+3) ============
// CalendarBookingDialog — Simulated 7-day availability + 1-click booking.
// WhatsAppButton — demo-locked send (paid mode tease).

export function CalendarBookingDialog({ leadId, leadName, onBooked }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [days, setDays] = useState([]); // [{day_label, slots: [...]}]

  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCalendarAvailability(leadId, 7);
      const grouped = {};
      for (const s of data?.slots || []) {
        if (!grouped[s.day_label]) grouped[s.day_label] = [];
        grouped[s.day_label].push(s);
      }
      setDays(
        Object.entries(grouped).map(([label, slots]) => ({
          day_label: label,
          slots,
        })),
      );
    } catch (e) {
      toast.error("Couldn't fetch availability");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (open) fetchAvailability();
  }, [open, fetchAvailability]);

  const onBook = async (slot) => {
    if (slot.is_busy) return;
    setSubmitting(true);
    try {
      const res = await bookCalendarMeeting(leadId, slot.start);
      toast.success("Meeting booked!", {
        description: `${slot.day_label} · ${slot.label}`,
      });
      setOpen(false);
      if (typeof onBooked === "function") onBooked(res?.meeting);
    } catch (e) {
      toast.error("Booking failed", {
        description: e?.response?.data?.detail || "Try a different slot.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="book-meeting-btn"
        className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
      >
        <CalendarCheck className="h-3.5 w-3.5 mr-1" /> Book meeting
      </Button>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        data-testid="calendar-booking-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <CalendarCheck className="h-5 w-5 text-emerald-600" />
            Book a meeting with {leadName || "this lead"}
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Pick a 30-min slot. Demo workspace uses simulated availability —
            hook up Google or Outlook in the paid tier for real free/busy
            sync.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-12 text-center text-slate-500 text-[13px]">
            Loading availability…
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            {days.map((d) => (
              <div key={d.day_label} data-testid={`calendar-day-${d.day_label}`}>
                <div className="text-[12px] font-bold uppercase tracking-wider text-slate-700 mb-2">
                  {d.day_label}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {d.slots.map((s) => (
                    <button
                      type="button"
                      key={s.start}
                      onClick={() => onBook(s)}
                      disabled={s.is_busy || submitting}
                      data-testid={`calendar-slot-${s.start}`}
                      data-slot-busy={s.is_busy ? "true" : "false"}
                      className={`text-[12.5px] font-semibold rounded-md border px-2 py-1.5 transition-colors ${
                        s.is_busy
                          ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed line-through"
                          : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-400"
                      }`}
                      title={s.is_busy ? "Busy" : "Click to book"}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function WhatsAppButton({ leadId, leadName, fallbackMessage }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const msg =
        fallbackMessage ||
        `Hi ${leadName?.split(" ")?.[0] || "there"} — wanted to ping you on the AI follow-up I sent earlier. Quick chat tomorrow?`;
      const res = await sendWhatsAppMessage(leadId, msg);
      if (res?.status === "locked") {
        toast.info("WhatsApp enabled in paid mode", {
          description: "Connect your WhatsApp Business account to send live.",
          icon: <Lock className="h-4 w-4" />,
        });
      } else {
        toast.success("WhatsApp message sent");
      }
    } catch (e) {
      toast.error("WhatsApp send failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={onClick}
      disabled={busy}
      data-testid="whatsapp-send-btn"
      className="h-9 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
    >
      <MessageCircle className="h-3.5 w-3.5 mr-1" />
      WhatsApp
      <Lock className="h-3 w-3 ml-1.5 opacity-50" />
    </Button>
  );
}

